import * as THREE from "three";
import { IWall } from "../models/abstract/Wall";

import {
  DEFAULT_SNAP_DISTANCE_PARAM_VALUE, SNAP_THRESHOLD
} from "./constants";

/**
 * Get the closest point within snapping distance on the first wall to the given coordinates.
 *
 * @param {Array<WallModel>} wallModels - An array of WallModel objects representing the walls.
 * @param {number} x - The x coordinate to find the closest point to.
 * @param {number} y - The y coordinate to find the closest point to.
 * @param {number} snapDistance - The maximum distance for a point to be considered as a snap point.
 * @param {Map} [distanceCache] - An optional cache to store previously calculated distances.
 * @returns {Array<number>} The closest point [x, y] within the snap distance or the original coordinates if no such point is found.
 */
export const getFirstWallStartSnapPoint = (
  wallModels: IWall[],
  x: number,
  y: number,
  snapDistance: number = DEFAULT_SNAP_DISTANCE_PARAM_VALUE,
  distanceCache = new Map(),
): THREE.Vector3 => {
  if (!Number.isFinite(snapDistance)) {
    throw new Error("snapDistance must be a finite number");
  }

  // Define the points to consider for snapping
  const pointsToConsider =
    wallModels.length > 0 ? [wallModels[0].startPoint] : [];

  let closestPoint = new THREE.Vector3(x, y, 0);
  let smallestDistance = Infinity;

  // Loop through the points to consider
  for (const point of pointsToConsider) {
    const distanceKey = `${point.x},${point.y},${x},${y}`;
    let distance;
    // If distance for these points was already calculated, use cached value
    if (distanceCache.has(distanceKey)) {
      distance = distanceCache.get(distanceKey);
    } else {
      // Calculate distance and cache it
      distance = Math.hypot(point.x - x, point.y - y);
      distanceCache.set(distanceKey, distance);
    }
    if (distance < smallestDistance && distance < snapDistance) {
      smallestDistance = distance;
      closestPoint = point;
    }
  }

  return closestPoint;
};

const EXTEND = 100;

export const getSnappedPoint = (
  x: number,
  y: number,
  wallModels: IWall[],
  snapThreshold: number = SNAP_THRESHOLD,
) => {
  const snappedPoint = new THREE.Vector3(x, y, 0);
  const newSnapLines: any[] = [];
  let snapOccurred = false;

  // Closest horizontal/vertical snap candidates
  let bestH: { delta: number; target: THREE.Vector3 } | null = null;
  let bestV: { delta: number; target: THREE.Vector3 } | null = null;

  for (const wall of wallModels) {
    for (const p of [wall.startPoint, wall.endPoint]) {
      const dy = Math.abs(p.y - y);
      if (dy < snapThreshold && (!bestH || dy < bestH.delta)) {
        bestH = { delta: dy, target: p.clone() };
      }

      const dx = Math.abs(p.x - x);
      if (dx < snapThreshold && (!bestV || dx < bestV.delta)) {
        bestV = { delta: dx, target: p.clone() };
      }
    }
  }

  // Horizontal snap + guide (y aligned)
  if (bestH) {
    snappedPoint.y = bestH.target.y;
    snapOccurred = true;

    const xMin = Math.min(snappedPoint.x, bestH.target.x) - EXTEND;
    const xMax = Math.max(snappedPoint.x, bestH.target.x) + EXTEND;

    // if same x (rare), still draw a visible line
    const a = Number.isFinite(xMin) ? xMin : snappedPoint.x - EXTEND;
    const b = Number.isFinite(xMax) ? xMax : snappedPoint.x + EXTEND;

    newSnapLines.push({
      point1: [a, snappedPoint.y],
      point2: [b, snappedPoint.y],
      color: "#009fe3",
    });
  }

  // Vertical snap + guide (x aligned)
  if (bestV) {
    snappedPoint.x = bestV.target.x;
    snapOccurred = true;

    const yMin = Math.min(snappedPoint.y, bestV.target.y) - EXTEND;
    const yMax = Math.max(snappedPoint.y, bestV.target.y) + EXTEND;

    const a = Number.isFinite(yMin) ? yMin : snappedPoint.y - EXTEND;
    const b = Number.isFinite(yMax) ? yMax : snappedPoint.y + EXTEND;

    newSnapLines.push({
      point1: [snappedPoint.x, a],
      point2: [snappedPoint.x, b],
      color: "#009fe3",
    });
  }

  return { snappedPoint, newSnapLines, snapOccurred };
};